import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { WorkflowDto, ResponseDtoWorkflowDto, ResponseDtoListWorkflowDto, ResponseDtoString } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

/**
 * Hook to fetch all workflows
 * @returns Query result with all workflows
 */
export const useWorkflowList = (): UseQueryResult<ResponseDtoListWorkflowDto> =>
  useQuery(['workflows'], async () => {
    const response = await tFinancialService.api.getAllWorkflows();
    return response.data;
  });

/**
 * Hook to fetch a single workflow by ID
 * @param id Workflow ID (UUID)
 * @returns Query result with workflow data
 */
export const useWorkflowById = (id: string): UseQueryResult<ResponseDtoWorkflowDto> =>
  useQuery(
    ['workflow', id],
    async () => {
      const response = await tFinancialService.api.getWorkflowById(id);
      return response.data;
    },
    {
      enabled: !!id, // Only fetch if id is provided
    }
  );

/**
 * Hook to create or update a workflow
 * @returns Mutation function for creating/updating workflows
 */
export const useUpsertWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (workflow: WorkflowDto): Promise<ResponseDtoWorkflowDto> => {
      // Using createOrUpdateWorkflow for both creating and updating workflows
      const response = await tFinancialService.api.createOrUpdateWorkflow(workflow);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['workflows']);
      },
    }
  );
};

/**
 * Hook to delete a workflow
 * @returns Mutation function for deleting workflows
 */
export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: string): Promise<ResponseDtoString> => {
      const response = await tFinancialService.api.deleteWorkflowById(id);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['workflows']);
      },
    }
  );
};
